import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useState, useRef, useEffect } from 'react'
import { CATEGORIES, TOP_NAV } from '@/registry'
import { resolveLocaleFromPath, toCurrentLocalePath, toLocalePath } from '@/i18n/routing'
import { getLocaleLabel, type LocaleCode } from '@/i18n/locales'
import { getAvailableLocalesForPath } from '@/i18n/availability'

const LOCALE_BADGES: Record<LocaleCode, string> = {
  en: 'EN',
  es: 'ES',
  'pt-PT': 'PT',
  de: 'DE',
  fr: 'FR',
}

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false)
  const [calcDropOpen, setCalcDropOpen] = useState(false)
  const [localeDropOpen, setLocaleDropOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const navigate = useNavigate()
  const { pathname, search, hash } = useLocation()
  const calcDropRef = useRef<HTMLDivElement>(null)
  const localeDropRef = useRef<HTMLDivElement>(null)

  const calcSubcategories = CATEGORIES.filter(c => c.group === 'calculators')
  const localize = (path: string) => toCurrentLocalePath(pathname, path)
  const { locale: currentLocale, pathWithoutLocalePrefix } = resolveLocaleFromPath(pathname)
  const availableLocales = getAvailableLocalesForPath(pathWithoutLocalePrefix)
  const switcherLocales = availableLocales.includes(currentLocale)
    ? availableLocales
    : [currentLocale, ...availableLocales]

  const handleLocaleChange = (newLocale: LocaleCode) => {
    const localizedPath = toLocalePath(pathWithoutLocalePrefix, newLocale)
    navigate(`${localizedPath}${search}${hash}`)
    setLocaleDropOpen(false)
    setMenuOpen(false)
  }

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (calcDropRef.current && !calcDropRef.current.contains(e.target as Node)) {
        setCalcDropOpen(false)
      }
      if (localeDropRef.current && !localeDropRef.current.contains(e.target as Node)) {
        setLocaleDropOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      navigate(`${localize('/')}?q=${encodeURIComponent(searchQuery.trim())}`)
      setSearchQuery('')
      setMenuOpen(false)
    }
  }

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to={localize('/')} className="flex items-center gap-2 font-bold text-xl text-brand-700 shrink-0">
            <span className="text-brand-600">⚙</span>
            <span>Tool<span className="text-brand-600">vance</span></span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1 text-sm font-medium">
            {/* Calculators with dropdown */}
            <div className="relative" ref={calcDropRef}>
              <button
                onClick={() => setCalcDropOpen(o => !o)}
                className={`flex items-center gap-1 px-3 py-2 rounded-md transition-colors ${
                  calcDropOpen ? 'text-brand-600 bg-brand-50' : 'text-gray-600 hover:text-brand-600 hover:bg-gray-50'
                }`}
                aria-expanded={calcDropOpen}
                aria-haspopup="true"
              >
                Calculators
                <svg className={`w-4 h-4 transition-transform ${calcDropOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {calcDropOpen && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-lg border border-gray-200 shadow-lg py-1 z-50">
                  <Link
                    to={localize('/calculators')}
                    onClick={() => setCalcDropOpen(false)}
                    className="block px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider hover:bg-gray-50"
                  >
                    All Calculators
                  </Link>
                  <div className="border-t border-gray-100 my-1" />
                  {calcSubcategories.map(cat => (
                    <Link
                      key={cat.id}
                      to={localize(`/${cat.id}`)}
                      onClick={() => setCalcDropOpen(false)}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-brand-600"
                    >
                      <span>{cat.icon}</span>
                      <span>{cat.label}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Converters, Tools, Generators */}
            {TOP_NAV.filter(n => n.id !== 'calculators').map(item => (
              <NavLink
                key={item.id}
                to={localize(item.path)}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md transition-colors ${
                    isActive ? 'text-brand-600 bg-brand-50' : 'text-gray-600 hover:text-brand-600 hover:bg-gray-50'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* Search + Language */}
          <div className="hidden sm:flex items-center gap-2">
            {switcherLocales.length > 0 && (
              <div className="relative" ref={localeDropRef}>
                <button
                  type="button"
                  onClick={() => setLocaleDropOpen(o => !o)}
                  className="h-8 w-8 rounded-full border border-gray-200 bg-white text-[11px] font-semibold text-gray-700 hover:bg-gray-50 inline-flex items-center justify-center"
                  aria-label="Language selector"
                  aria-expanded={localeDropOpen}
                  aria-haspopup="true"
                  title={getLocaleLabel(currentLocale)}
                >
                  <span className="leading-none">{LOCALE_BADGES[currentLocale]}</span>
                </button>

                {localeDropOpen && switcherLocales.length > 1 && (
                  <div className="absolute top-full right-0 mt-1 min-w-36 bg-white rounded-lg border border-gray-200 shadow-lg py-1 z-50">
                    {switcherLocales.map(locale => (
                      <button
                        key={locale}
                        type="button"
                        onClick={() => handleLocaleChange(locale)}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 ${
                          currentLocale === locale ? 'text-brand-700 font-medium' : 'text-gray-700'
                        }`}
                        aria-label={getLocaleLabel(locale)}
                      >
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 text-[11px] font-semibold text-gray-700">
                          {LOCALE_BADGES[locale]}
                        </span>
                        <span>{getLocaleLabel(locale)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <form onSubmit={handleSearch} className="flex items-center">
            <input
              type="search"
              placeholder="Search..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-40 lg:w-56 px-3 py-1.5 text-sm border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              aria-label="Search calculators"
            />
            <button
              type="submit"
              className="px-3 py-1.5 bg-brand-600 text-white text-sm rounded-r-md hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500"
              aria-label="Search"
            >
              Search
            </button>
            </form>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden p-2 text-gray-600 hover:text-brand-600"
            aria-label="Toggle navigation menu"
            aria-expanded={menuOpen}
          >
            {menuOpen ? '✕' : '☰'}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white px-4 py-3">
          {switcherLocales.length > 0 && (
            <div className="mb-4">
              <p className="block text-sm font-medium text-gray-700 mb-2">Language</p>
              <div className="flex items-center gap-2" aria-label="Language selector" role="group">
                {switcherLocales.map(locale => (
                  <button
                    key={locale}
                    type="button"
                    onClick={() => handleLocaleChange(locale)}
                    className={`h-8 w-8 rounded-full text-base leading-none border transition-colors ${
                      currentLocale === locale
                        ? 'bg-brand-600 text-white border-brand-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                    title={getLocaleLabel(locale)}
                    aria-label={getLocaleLabel(locale)}
                    aria-pressed={currentLocale === locale}
                  >
                    <span className="text-[11px] font-semibold leading-none">{LOCALE_BADGES[locale]}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <form onSubmit={handleSearch} className="flex mb-4">
            <input
              type="search"
              placeholder="Search..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-brand-500"
              aria-label="Search calculators"
            />
            <button type="submit" className="px-3 py-2 bg-brand-600 text-white text-sm rounded-r-md">Go</button>
          </form>

          {/* Calculators section */}
          <div className="mb-3">
            <Link to={localize('/calculators')} className="font-semibold text-gray-800 block py-1" onClick={() => setMenuOpen(false)}>
              🧮 Calculators
            </Link>
            <div className="pl-4 space-y-1 mt-1">
              {calcSubcategories.map(cat => (
                <Link
                  key={cat.id}
                  to={localize(`/${cat.id}`)}
                  className="flex items-center gap-2 text-gray-600 hover:text-brand-600 py-1 text-sm"
                  onClick={() => setMenuOpen(false)}
                >
                  <span>{cat.icon}</span><span>{cat.label}</span>
                </Link>
              ))}
            </div>
          </div>

          {TOP_NAV.filter(n => n.id !== 'calculators').map(item => (
            <Link
              key={item.id}
              to={localize(item.path)}
              className="block font-semibold text-gray-800 py-2 hover:text-brand-600"
              onClick={() => setMenuOpen(false)}
            >
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </header>
  )
}
